import type { ReactNode } from "react";
import { Bike, ShoppingBag, Store, X } from "lucide-react";
import { Pill, type PillVariant } from "@veorest/ui";
import {
  partnerStageTranslationKey,
  type PartnerOrderRow,
  type PartnerOrderStage,
} from "../../lib/partnerOrders";
import { useI18n } from "../../i18n";

interface OverviewListPanelProps {
  title: string;
  subtitle: string;
  icon?: ReactNode;
  children: ReactNode;
}

function OverviewListPanel({ title, subtitle, icon, children }: OverviewListPanelProps) {
  return (
    <section className="overview-panel">
      <header className="overview-panel__header">
        {icon ? (
          <span className="overview-panel__icon" aria-hidden>
            {icon}
          </span>
        ) : null}
        <div className="min-w-0">
          <h3 className="overview-panel__title">{title}</h3>
          <p className="overview-panel__subtitle">{subtitle}</p>
        </div>
      </header>
      <div className="overview-panel__body">{children}</div>
    </section>
  );
}

const STAGE_PILL: Record<PartnerOrderStage, PillVariant> = {
  pending: "warning",
  processing: "info",
  ready: "accent",
  in_delivery: "info",
  completed: "success",
  cancelled: "danger",
};

function formatTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

interface PartnerRecentOrdersProps {
  orders: PartnerOrderRow[];
  filterStage?: PartnerOrderStage | null;
  onClearFilter?: () => void;
  onOrderTap?: (order: PartnerOrderRow) => void;
}

/** Client Overview recent-orders panel — partner fields / stages. */
export function PartnerRecentOrders({
  orders,
  filterStage = null,
  onClearFilter,
  onOrderTap,
}: PartnerRecentOrdersProps) {
  const { t } = useI18n();

  return (
    <OverviewListPanel
      title={t("overview.recentOrders.title")}
      subtitle={t("overview.recentOrders.subtitle")}
      icon={<ShoppingBag className="h-4 w-4" strokeWidth={2.25} />}
    >
      {filterStage ? (
        <div className="overview-filter-chip">
          <span>
            {t("overview.recentOrders.filteredBy")}{" "}
            <strong>{t(partnerStageTranslationKey(filterStage))}</strong>
          </span>
          <button
            type="button"
            onClick={onClearFilter}
            className="overview-filter-chip__clear"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            {t("common.clear")}
          </button>
        </div>
      ) : null}

      {orders.length === 0 ? (
        <div className="overview-panel-empty">
          <span className="overview-panel-empty__icon" aria-hidden>
            <ShoppingBag className="h-6 w-6" strokeWidth={2} />
          </span>
          <p className="overview-panel-empty__title">
            {t("overview.recentOrders.empty")}
          </p>
        </div>
      ) : (
        <ul className="overview-list">
          {orders.map((order) => (
            <li key={order.id}>
              <button
                type="button"
                onClick={() => onOrderTap?.(order)}
                className="overview-list-row"
              >
                <div
                  className="overview-list-row__avatar"
                  style={{
                    background: "color-mix(in srgb, var(--accent) 14%, transparent)",
                    color: "var(--accent)",
                  }}
                  aria-hidden
                >
                  <Store className="h-4 w-4" strokeWidth={2.25} />
                </div>
                <div className="overview-list-row__main min-w-0">
                  <p className="overview-list-row__title">
                    {order.displayNumber}
                    <span> · {order.tenant_name}</span>
                  </p>
                  <p className="overview-list-row__meta">
                    {order.customer_name}
                    {order.driver_name ? (
                      <>
                        {" · "}
                        <Bike className="inline h-3 w-3" strokeWidth={2} />{" "}
                        {order.driver_name}
                      </>
                    ) : null}
                    {" · "}
                    {order.fulfillment_type === "pickup"
                      ? t("common.pickup")
                      : t("common.delivery")}
                  </p>
                </div>
                <div className="overview-list-row__end">
                  <Pill variant={STAGE_PILL[order.stage]}>
                    {t(partnerStageTranslationKey(order.stage))}
                  </Pill>
                  <span className="overview-list-row__time">
                    {formatTime(order.created_at)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </OverviewListPanel>
  );
}
